/**
 * Midline-Based Collision Detection
 * 
 * Intuitive drag targeting using "children columns" and midline boundaries.
 * Replaces zone-based detection with Enter/Shift+Enter-like behavior.
 */

import type {
  CollisionConfig,
  CollisionNode,
  DropTarget,
  FlowRect,
} from './types';
import type { MindmapLayoutDirection } from '@/features/mindmap/core/MindmapTypes';

interface ChildrenColumn {
  parentId: string | null;
  rect: FlowRect;
  children: CollisionNode[];
  layoutDirection: MindmapLayoutDirection;
}

interface MidlineSlot {
  parentId: string | null;
  index: number;
  midlineMin: number;  // Start of this slot along stack axis
  midlineMax: number;  // End of this slot along stack axis
  isFirst: boolean;
  isLast: boolean;
}

export class MidlineCollisionDetector {
  private columns: ChildrenColumn[] = [];
  private slots: MidlineSlot[] = [];
  private config: CollisionConfig;
  private nodeMap: Map<string, CollisionNode> = new Map();
  private lastDropTarget: DropTarget | null = null;  // For stickiness
  private activeRootId: string | null = null;
  private activeDraggedId: string | null = null;

  constructor(config?: Partial<CollisionConfig>) {
    this.config = {
      layoutDirection: 'right',
      axisPadding: 32,
      crossPadding: 24,
      parentZoneDepth: 4,
      ...config,
    };
  }

  /**
   * Update configuration (e.g., when layout direction changes)
   */
  updateConfig(config: Partial<CollisionConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Build collision structures from current node layout.
   * Call when drag starts or layout changes significantly.
   */
  buildStructures(
    draggedId: string,
    nodes: CollisionNode[],
    rootId: string
  ): void {
    this.columns = [];
    this.slots = [];
    this.nodeMap = new Map(nodes.map(n => [n.id, n]));
    this.activeRootId = rootId;
    this.activeDraggedId = draggedId;
    this.lastDropTarget = null;

    // Group nodes by parent
    const nodesByParent = new Map<string | null, CollisionNode[]>();
    for (const node of nodes) {
      if (node.id === draggedId) continue;
      if (this.isDescendant(node.id, draggedId)) continue;
      
      const parentId = node.parentId;
      if (!nodesByParent.has(parentId)) {
        nodesByParent.set(parentId, []);
      }
      nodesByParent.get(parentId)!.push(node);
    }

    // Build children columns for each parent
    for (const [parentId, children] of nodesByParent) {
      if (children.length === 0) continue;

      // Skip collapsed parents
      const parent = parentId ? this.nodeMap.get(parentId) : null;
      // Note: We'd need collapsed state here, for now assume visible

      // Sort by layout order
      const sorted = [...children].sort((a, b) => a.order - b.order);

      // Compute bounding rectangle for all children
      const rect = this.computeBoundingRect(sorted);

      this.columns.push({
        parentId,
        rect,
        children: sorted,
        layoutDirection: this.config.layoutDirection,
      });

      // Build midline slots for this column
      this.buildMidlineSlots(parentId, sorted);
    }
  }

  /**
   * Find the best drop target for the current pointer position.
   * Uses sticky behavior to prevent flapping.
   */
  findBestTarget(
    pointerFlow: { x: number; y: number },
    draggedId: string,
    rootId: string
  ): DropTarget | null {
    // Step 1: Determine mode (sibling vs child)
    const mode = this.determineMode(pointerFlow, draggedId);

    if (!mode) {
      // Outside all columns and parent zones
      this.lastDropTarget = null;
      return null;
    }

    let target: DropTarget | null = null;

    if (mode.type === 'sibling') {
      // Find closest midline slot in this column
      target = this.findMidlineSlot(pointerFlow, mode.parentId);
    } else {
      // Child mode: append to nearest parent column
      target = this.buildChildTarget(mode.parentNodeId);
    }

    // Illegal root move check
    if (draggedId === rootId && target && target.parentId !== null) {
      this.lastDropTarget = null;
      return null;
    }

    // Apply stickiness: only change if different (parentId, index)
    if (this.lastDropTarget && target) {
      if (
        this.lastDropTarget.parentId === target.parentId &&
        this.lastDropTarget.index === target.index
      ) {
        // Same slot - keep previous target for stability
        return this.lastDropTarget;
      }
    }

    this.lastDropTarget = target;
    return target;
  }

  /**
   * Get all columns (for debugging/visualization)
   */
  getColumns(): readonly ChildrenColumn[] {
    return this.columns;
  }

  /**
   * Get all midline slots (for debugging/visualization)
   */
  getSlots(): readonly MidlineSlot[] {
    return this.slots;
  }

  // ============================================================================
  // Private: Mode Detection
  // ============================================================================

  private determineMode(
    pointer: { x: number; y: number },
    draggedId: string
  ): { type: 'sibling'; parentId: string | null } | { type: 'child'; parentNodeId: string } | null {
    const { layoutDirection } = this.config;

    // Check if pointer is inside any children column (sibling mode)
    for (const column of this.columns) {
      if (this.isPointInRect(pointer, column.rect)) {
        const targetParentId = column.parentId ?? this.activeRootId;
        const draggedParentId = this.nodeMap.get(draggedId)?.parentId ?? null;

        if (
          targetParentId &&
          targetParentId !== draggedParentId &&
          targetParentId !== draggedId &&
          !this.isDescendant(targetParentId, draggedId) &&
          this.isNearParentEdge(pointer, column)
        ) {
          return { type: 'child', parentNodeId: targetParentId };
        }
        return { type: 'sibling', parentId: column.parentId };
      }

      const withinAxis = (() => {
        if (layoutDirection === 'right' || layoutDirection === 'left') {
          return pointer.x >= column.rect.x && pointer.x <= column.rect.x + column.rect.width;
        }
        return pointer.y >= column.rect.y && pointer.y <= column.rect.y + column.rect.height;
      })();

      if (!withinAxis) continue;

      const axisPadding = this.config.axisPadding * 2;

      if (layoutDirection === 'right' || layoutDirection === 'left') {
        const above = pointer.y >= column.rect.y - axisPadding && pointer.y < column.rect.y;
        const below = pointer.y > column.rect.y + column.rect.height && pointer.y <= column.rect.y + column.rect.height + axisPadding;
        if (above || below) {
          return { type: 'sibling', parentId: column.parentId };
        }
      } else {
        const leftSide = pointer.x >= column.rect.x - axisPadding && pointer.x < column.rect.x;
        const rightSide = pointer.x > column.rect.x + column.rect.width && pointer.x <= column.rect.x + column.rect.width + axisPadding;
        if (leftSide || rightSide) {
          return { type: 'sibling', parentId: column.parentId };
        }
      }
    }

    // Check if pointer is on the child side of any parent (child mode)
    for (const [nodeId, node] of this.nodeMap) {
      if (nodeId === draggedId) continue;
      if (this.isDescendant(nodeId, draggedId)) continue;

      const childSideZone = this.buildChildSideZone(node);
      if (this.isPointInRect(pointer, childSideZone)) {
        return { type: 'child', parentNodeId: nodeId };
      }
    }

    return null;
  }

  /**
   * Build zone on the "child side" of a node based on layout direction
   */
  private buildChildSideZone(node: CollisionNode): FlowRect {
    const { layoutDirection, axisPadding, crossPadding, parentZoneDepth } = this.config;
    const pos = node.position;
    const dim = node.dimensions;
    const depthWidth = axisPadding * (parentZoneDepth + 1);

    switch (layoutDirection) {
      case 'right':
        return {
          x: pos.x + dim.width,
          y: pos.y - crossPadding,
          width: depthWidth,
          height: dim.height + crossPadding * 2,
        };
      case 'left':
        return {
          x: pos.x - depthWidth,
          y: pos.y - crossPadding,
          width: depthWidth,
          height: dim.height + crossPadding * 2,
        };
      case 'down':
        return {
          x: pos.x - crossPadding,
          y: pos.y + dim.height,
          width: dim.width + crossPadding * 2,
          height: depthWidth,
        };
      case 'up':
        return {
          x: pos.x - crossPadding,
          y: pos.y - depthWidth,
          width: dim.width + crossPadding * 2,
          height: depthWidth,
        };
    }
  }

  // ============================================================================
  // Private: Midline Slot Detection
  // ============================================================================

  private buildMidlineSlots(parentId: string | null, children: CollisionNode[]): void {
    if (children.length === 0) return;

    const { layoutDirection } = this.config;
    const isVerticalStack = layoutDirection === 'right' || layoutDirection === 'left';

    // Sort by center position along stack axis
    const sorted = [...children].sort((a, b) => {
      const centerA = isVerticalStack
        ? a.position.y + a.dimensions.height / 2
        : a.position.x + a.dimensions.width / 2;
      const centerB = isVerticalStack
        ? b.position.y + b.dimensions.height / 2
        : b.position.x + b.dimensions.width / 2;
      return centerA - centerB;
    });

    // Compute midlines between siblings
    const midlines: number[] = [];

    for (let i = 0; i <= sorted.length; i++) {
      if (i === 0) {
        // Before first child: start at leading edge
        const firstNode = sorted[0];
        const edge = isVerticalStack
          ? firstNode.position.y
          : firstNode.position.x;
        midlines.push(edge);
      } else if (i === sorted.length) {
        // After last child: end at trailing edge
        const lastNode = sorted[sorted.length - 1];
        const edge = isVerticalStack
          ? lastNode.position.y + lastNode.dimensions.height
          : lastNode.position.x + lastNode.dimensions.width;
        midlines.push(edge);
      } else {
        // Between two children: compute midpoint
        const prevNode = sorted[i - 1];
        const nextNode = sorted[i];
        const prevEnd = isVerticalStack
          ? prevNode.position.y + prevNode.dimensions.height
          : prevNode.position.x + prevNode.dimensions.width;
        const nextStart = isVerticalStack
          ? nextNode.position.y
          : nextNode.position.x;
        midlines.push((prevEnd + nextStart) / 2);
      }
    }

    // Create slots between midlines
    for (let i = 0; i < midlines.length - 1; i++) {
      this.slots.push({
        parentId,
        index: i,
        midlineMin: midlines[i],
        midlineMax: midlines[i + 1],
        isFirst: i === 0,
        isLast: i === midlines.length - 2,
      });
    }
  }

  private findMidlineSlot(
    pointer: { x: number; y: number },
    parentId: string | null
  ): DropTarget | null {
    const { layoutDirection } = this.config;
    const isVerticalStack = layoutDirection === 'right' || layoutDirection === 'left';

    // Get pointer coordinate along stack axis
    const pointerPos = isVerticalStack ? pointer.y : pointer.x;

    // Find slots for this parent
    const parentSlots = this.slots.filter(s => s.parentId === parentId);

    if (parentSlots.length === 0) return null;

    // Find slot containing pointer
    for (const slot of parentSlots) {
      if (pointerPos >= slot.midlineMin && pointerPos < slot.midlineMax) {
        // Get column for this parent to build rect
        const column = this.columns.find(c => c.parentId === parentId);
        if (!column) return null;

        // Build slot rectangle
        const rect: FlowRect = isVerticalStack
          ? {
              x: column.rect.x,
              y: slot.midlineMin,
              width: column.rect.width,
              height: slot.midlineMax - slot.midlineMin,
            }
          : {
              x: slot.midlineMin,
              y: column.rect.y,
              width: slot.midlineMax - slot.midlineMin,
              height: column.rect.height,
            };

        return {
          type: 'sibling-slot',
          parentId,
          index: slot.index,
          rect,
          distance: 0,
        };
      }
    }

    // Outside all slots - use closest boundary (first or last)
    const first = parentSlots[0];
    const last = parentSlots[parentSlots.length - 1];

    if (pointerPos < first.midlineMin) {
      // Before first slot
      const column = this.columns.find(c => c.parentId === parentId);
      if (!column) return null;

      const rect: FlowRect = isVerticalStack
        ? { x: column.rect.x, y: first.midlineMin - this.config.axisPadding, width: column.rect.width, height: this.config.axisPadding }
        : { x: first.midlineMin - this.config.axisPadding, y: column.rect.y, width: this.config.axisPadding, height: column.rect.height };

      return {
        type: 'sibling-slot',
        parentId,
        index: 0,
        rect,
        distance: 0,
      };
    } else {
      // After last slot
      const column = this.columns.find(c => c.parentId === parentId);
      if (!column) return null;

      const rect: FlowRect = isVerticalStack
        ? { x: column.rect.x, y: last.midlineMax, width: column.rect.width, height: this.config.axisPadding }
        : { x: last.midlineMax, y: column.rect.y, width: this.config.axisPadding, height: column.rect.height };

      return {
        type: 'sibling-slot',
        parentId,
        index: last.index + 1,
        rect,
        distance: 0,
      };
    }
  }

  private buildChildTarget(parentNodeId: string): DropTarget | null {
    const parent = this.nodeMap.get(parentNodeId);
    if (!parent) return null;

    // Count existing children (excluding dragged)
    const childCount = Array.from(this.nodeMap.values())
      .filter(n => n.parentId === parentNodeId && n.id !== this.activeDraggedId)
      .length;

    // Build zone rect
    const zone = this.buildChildSideZone(parent);

    return {
      type: 'parent-zone',
      parentId: parentNodeId,
      index: childCount,
      targetNodeId: parentNodeId,
      rect: zone,
      distance: 0,
    };
  }

  // ============================================================================
  // Private: Helpers
  // ============================================================================

  private computeBoundingRect(nodes: CollisionNode[]): FlowRect {
    if (nodes.length === 0) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const node of nodes) {
      const { position, dimensions } = node;
      minX = Math.min(minX, position.x);
      minY = Math.min(minY, position.y);
      maxX = Math.max(maxX, position.x + dimensions.width);
      maxY = Math.max(maxY, position.y + dimensions.height);
    }

    // Add padding around the column
    const padding = this.config.crossPadding;

    return {
      x: minX - padding,
      y: minY - padding,
      width: maxX - minX + padding * 2,
      height: maxY - minY + padding * 2,
    };
  }

  private isDescendant(candidateId: string, ancestorId: string): boolean {
    if (candidateId === ancestorId) return true;

    let current = this.nodeMap.get(candidateId);
    while (current?.parentId) {
      if (current.parentId === ancestorId) return true;
      current = this.nodeMap.get(current.parentId);
    }

    return false;
  }

  private isPointInRect(point: { x: number; y: number }, rect: FlowRect): boolean {
    return (
      point.x >= rect.x &&
      point.x <= rect.x + rect.width &&
      point.y >= rect.y &&
      point.y <= rect.y + rect.height
    );
  }

  private isNearParentEdge(
    pointer: { x: number; y: number },
    column: ChildrenColumn
  ): boolean {
    if (!column.parentId && !this.activeRootId) return false;

    const { layoutDirection, axisPadding, parentZoneDepth } = this.config;
    const threshold = axisPadding * parentZoneDepth;

    switch (layoutDirection) {
      case 'right': {
        const distance = pointer.x - column.rect.x;
        return distance >= 0 && distance <= threshold;
      }
      case 'left': {
        const distance = column.rect.x + column.rect.width - pointer.x;
        return distance >= 0 && distance <= threshold;
      }
      case 'down': {
        const distance = pointer.y - column.rect.y;
        return distance >= 0 && distance <= threshold;
      }
      case 'up': {
        const distance = column.rect.y + column.rect.height - pointer.y;
        return distance >= 0 && distance <= threshold;
      }
      default:
        return false;
    }
  }
}
